import { useEffect, useState } from "react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api, { ROLE_LABELS, apiErr } from "@/lib/api";
import { numLang } from "@/lib/num";
import { tx } from "@/lib/txnI18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useTableTools, TableToolbar, SelectAllBox, SelectBox, deleteEach } from "@/components/TableTools";
import { Plus, KeyRound, Building2, ChevronDown, ChevronRight, Trash2, Pencil, Download, ShieldCheck } from "lucide-react";

const ROLES = ["super_admin", "staff_keuangan", "staff_pajak", "leader", "supervisor", "client_staff", "client_backup"];
const PROTECTED_EMAIL = "bisnisnatalya@gmail.com"; // root super admin: cannot be deactivated/deleted

export default function Users() {
  const { t } = useI18n();
  const { clients, loadClients } = useAuth();
  const [users, setUsers] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [showUsers, setShowUsers] = useState(true);
  const [showClients, setShowClients] = useState(true);
  const [showInactiveUsers, setShowInactiveUsers] = useState(false);
  const [showInactiveClients, setShowInactiveClients] = useState(false);

  const load = async () => { const { data } = await api.get(`/users?include_inactive=${showInactiveUsers ? 1 : 0}`); setUsers(data); };
  const loadAllClients = async () => { const { data } = await api.get(`/clients?include_inactive=${showInactiveClients ? 1 : 0}`); setAllClients(data); };
  const reloadClients = () => { loadClients(); loadAllClients(); };
  useEffect(() => { load(); }, [showInactiveUsers]);
  useEffect(() => { loadAllClients(); }, [showInactiveClients]);

  const toggleActive = async (u) => {
    await api.put(`/users/${u.id}`, { is_active: !u.is_active });
    toast.success(u.is_active ? t("common.inactive") : t("common.active")); load();
  };
  const resetPass = async (u) => {
    const p = window.prompt(`Kata sandi baru untuk ${u.name}:`);
    if (!p) return;
    await api.put(`/users/${u.id}`, { password: p }); toast.success("Kata sandi direset");
  };
  const delUser = async (u) => {
    if (!window.confirm(`Nonaktifkan pengguna ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Pengguna dinonaktifkan"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const delClient = async (c) => {
    if (!window.confirm(`Nonaktifkan klien ${c.name}? Semua pengguna klien ini ikut dinonaktifkan.`)) return;
    try { await api.delete(`/clients/${c.id}`); toast.success("Klien dinonaktifkan"); reloadClients(); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const exportDir = async (type) => {
    try {
      const res = await api.get(`/export/directory?type=${type}&fmt=xlsx&lang=${numLang()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${type}.xlsx`; a.click();
      URL.revokeObjectURL(url); toast.success(tx("exportOk", numLang()));
    } catch (e) { toast.error("Gagal export"); }
  };

  const clientName = (id) => allClients.find((c) => c.id === id)?.name || clients.find((c) => c.id === id)?.name || "—";
  const uTools = useTableTools(users, { date: (u) => u.created_at, text: (u) => `${u.name} ${u.email} ${ROLE_LABELS[u.role]} ${clientName(u.client_id)} ${u.phone || ""}` });
  const cTools = useTableTools(allClients, { date: (c) => c.created_at, text: (c) => `${c.name} ${c.code} ${c.phone || ""} ${c.npwp || ""}` });
  const fUsers = uTools.view;
  const fClients = cTools.view;
  const bulkDelUsers = async (ids) => {
    const own = ids.filter((id) => users.find((u) => u.id === id)?.email !== PROTECTED_EMAIL);
    const r = await deleteEach(own, (id) => api.delete(`/users/${id}`));
    load();
    return { failed: r.failed + ids.length - own.length, message: r.message };
  };
  const uCols = [[t("common.name"), "name"], [t("common.email"), "email"], [t("common.role"), (u) => ROLE_LABELS[u.role]], [t("common.client"), (u) => (u.client_id ? clientName(u.client_id) : "")],
    [t("common.phone"), "phone"], [t("common.status"), (u) => (u.is_active ? t("common.active") : t("common.inactive"))]];
  const cCols = [[t("common.name"), "name"], [t("common.code"), "code"], [t("common.phone"), "phone"], ["NPWP", "npwp"], [t("common.status"), (c) => (c.is_active === false ? t("common.inactive") : t("common.active"))]];

  return (
    <div className="space-y-4" data-testid="users-page">
      <div className="page-sticky">
        <h1 className="font-head text-2xl font-bold tracking-tight">{t("users.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("users.desc")}</p>
      </div>

      {/* USERS TABLE */}
      <Card className="overflow-hidden">
        <div className="flex items-center justify-between p-4 flex-wrap gap-2">
          <button className="flex items-center gap-2 font-head font-semibold" onClick={() => setShowUsers((v) => !v)} data-testid="toggle-users-table">
            {showUsers ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            {t("users.usersTable")} <span className="text-muted-foreground font-normal text-sm">({fUsers.length})</span>
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground"><Switch checked={showInactiveUsers} onCheckedChange={setShowInactiveUsers} data-testid="toggle-show-inactive-users" />{t("users.showInactive")}</label>
            <TableToolbar tools={uTools} id="users" cols={uCols} filename="users" onDelete={bulkDelUsers}>
              <Button variant="outline" size="sm" className="h-9" onClick={() => exportDir("users")} data-testid="export-users"><Download className="h-4 w-4 mr-1" />Excel</Button>
              <UserForm clients={clients} onDone={load} />
            </TableToolbar>
          </div>
        </div>
        {showUsers && (
          <Table>
            <TableHeader><TableRow className="bg-secondary/50">
              <TableHead className="w-8"><SelectAllBox tools={uTools} id="users" /></TableHead>
              <TableHead>{t("common.name")}</TableHead><TableHead>{t("common.email")}</TableHead><TableHead>{t("common.role")}</TableHead>
              <TableHead>{t("common.client")}</TableHead><TableHead>{t("common.phone")}</TableHead><TableHead>{t("common.status")}</TableHead><TableHead className="text-right">{t("common.actions")}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {fUsers.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell><SelectBox tools={uTools} row={u} id="users" /></TableCell>
                  <TableCell className="text-sm font-medium">{u.name}</TableCell>
                  <TableCell className="text-xs">{u.email}</TableCell>
                  <TableCell className="text-xs">{ROLE_LABELS[u.role]}</TableCell>
                  <TableCell className="text-xs">{u.client_id ? clientName(u.client_id) : "—"}</TableCell>
                  <TableCell className="text-xs">{u.phone || "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {u.email === PROTECTED_EMAIL ? (
                        <span className="text-xs inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300 px-2 py-0.5" data-testid={`protected-badge-${u.email}`}><ShieldCheck className="h-3 w-3" />Dilindungi</span>
                      ) : (
                        <>
                          <Switch checked={u.is_active} onCheckedChange={() => toggleActive(u)} data-testid={`toggle-active-${u.email}`} />
                          <span className="text-xs">{u.is_active ? t("common.active") : t("common.inactive")}</span>
                        </>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <UserForm clients={clients} editUser={u} onDone={load} trigger={<Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`edit-user-${u.email}`}><Pencil className="h-4 w-4" /></Button>} />
                    <Button variant="ghost" size="icon" className="h-7 w-7" title="Reset Kata Sandi" onClick={() => resetPass(u)} data-testid={`reset-pass-${u.email}`}><KeyRound className="h-4 w-4" /></Button>
                    {u.email !== PROTECTED_EMAIL && (
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => delUser(u)} data-testid={`del-user-${u.email}`}><Trash2 className="h-4 w-4" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* CLIENTS TABLE */}
      <Card className="overflow-hidden">
        <div className="flex items-center justify-between p-4 flex-wrap gap-2">
          <button className="flex items-center gap-2 font-head font-semibold" onClick={() => setShowClients((v) => !v)} data-testid="toggle-clients-table">
            {showClients ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            {t("users.clientsTable")} <span className="text-muted-foreground font-normal text-sm">({fClients.length})</span>
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground"><Switch checked={showInactiveClients} onCheckedChange={setShowInactiveClients} data-testid="toggle-show-inactive-clients" />{t("users.showInactive")}</label>
            <TableToolbar tools={cTools} id="clients" cols={cCols} filename="clients">
              <Button variant="outline" size="sm" className="h-9" onClick={() => exportDir("clients")} data-testid="export-clients"><Download className="h-4 w-4 mr-1" />Excel</Button>
              <ClientForm onDone={reloadClients} />
            </TableToolbar>
          </div>
        </div>
        {showClients && (
          <Table>
            <TableHeader><TableRow className="bg-secondary/50">
              <TableHead className="w-8"><SelectAllBox tools={cTools} id="clients" /></TableHead>
              <TableHead>{t("common.name")}</TableHead><TableHead>{t("common.code")}</TableHead><TableHead>{t("common.phone")}</TableHead>
              <TableHead>{t("common.status")}</TableHead><TableHead className="text-right">{t("common.actions")}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {fClients.map((c) => (
                <TableRow key={c.id} data-testid={`client-row-${c.code}`}>
                  <TableCell><SelectBox tools={cTools} row={c} id="clients" /></TableCell>
                  <TableCell className="text-sm font-medium">{c.name}</TableCell>
                  <TableCell className="text-xs">{c.code}</TableCell>
                  <TableCell className="text-xs">{c.phone || "—"}</TableCell>
                  <TableCell><span className={`text-xs ${c.is_active === false ? "text-rose-600" : "text-emerald-600"}`}>{c.is_active === false ? t("common.inactive") : t("common.active")}</span></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <ClientForm editClient={c} onDone={reloadClients} trigger={<Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`edit-client-${c.code}`}><Pencil className="h-4 w-4" /></Button>} />
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => delClient(c)} data-testid={`del-client-${c.code}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function UserForm({ clients, editUser, onDone, trigger }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const blank = { name: "", email: "", password: "", role: "client_staff", client_id: "", phone: "", assigned_client_ids: [] };
  const [f, setF] = useState(blank);
  useEffect(() => {
    if (open) setF(editUser ? { name: editUser.name, email: editUser.email, password: "", role: editUser.role, client_id: editUser.client_id || "", phone: editUser.phone || "", assigned_client_ids: editUser.assigned_client_ids || [] } : blank);
  }, [open, editUser]);
  const isClient = ["client_staff", "client_backup"].includes(f.role);
  const isStaff = ["staff_keuangan", "staff_pajak", "leader"].includes(f.role);
  const toggleAssigned = (id) => setF((s) => ({ ...s, assigned_client_ids: (s.assigned_client_ids || []).includes(id) ? s.assigned_client_ids.filter((x) => x !== id) : [...(s.assigned_client_ids || []), id] }));
  const save = async () => {
    try {
      const assigned = isStaff ? (f.assigned_client_ids || []) : [];
      if (editUser) {
        const payload = { name: f.name, role: f.role, phone: f.phone, client_id: isClient ? f.client_id : null, assigned_client_ids: assigned };
        if (f.password) payload.password = f.password;
        await api.put(`/users/${editUser.id}`, payload);
      } else {
        const payload = { ...f, assigned_client_ids: assigned };
        if (!isClient) payload.client_id = null;
        await api.post("/users", payload);
      }
      toast.success("User disimpan"); setOpen(false); onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || <Button data-testid="add-user-btn"><Plus className="h-4 w-4 mr-2" />{t("users.newUser")}</Button>}</DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{editUser ? t("common.edit") : t("users.newUser")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>{t("common.name")}</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name" /></div>
          <div className="space-y-1.5"><Label>{t("common.email")}</Label><Input type="email" value={f.email} disabled={!!editUser} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email" /></div>
          <div className="space-y-1.5"><Label>{t("login.password")} {editUser && <span className="text-xs text-muted-foreground">(kosongkan jika tidak diubah)</span>}</Label><Input value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-password" /></div>
          <div className="space-y-1.5"><Label>{t("common.phone")}</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="0812xxxxxxx" data-testid="user-phone" /></div>
          <div className="space-y-1.5"><Label>{t("common.role")}</Label>
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
              <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {isClient && (
            <div className="space-y-1.5"><Label>{t("common.client")}</Label>
              <Select value={f.client_id} onValueChange={(v) => setF({ ...f, client_id: v })}>
                <SelectTrigger data-testid="user-client"><SelectValue placeholder="Pilih klien" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {isStaff && (
            <div className="space-y-1.5" data-testid="user-assigned-clients">
              <Label>Klien yang Dapat Diakses <span className="text-xs text-muted-foreground">(centang klien yang ditugaskan)</span></Label>
              <div className="rounded-md border border-border max-h-52 overflow-auto p-2 space-y-1">
                {clients.length === 0 && <p className="text-xs text-muted-foreground px-1 py-2">Belum ada klien.</p>}
                {clients.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-secondary/50 cursor-pointer text-sm" data-testid={`assign-client-${c.code || c.id}`}>
                    <Checkbox checked={(f.assigned_client_ids || []).includes(c.id)} onCheckedChange={() => toggleAssigned(c.id)} />
                    <span className="truncate">{c.name}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">Kosongkan untuk memberi akses ke semua klien (tanpa batasan).</p>
            </div>
          )}
        </div>
        <DialogFooter><Button onClick={save} data-testid="user-save">{t("common.save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ClientForm({ editClient, onDone, trigger }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", code: "", phone: "", npwp: "", address: "" });
  useEffect(() => {
    if (open) setF(editClient ? { name: editClient.name, code: editClient.code, phone: editClient.phone || "", npwp: editClient.npwp || "", address: editClient.address || "" } : { name: "", code: "", phone: "", npwp: "", address: "" });
  }, [open, editClient]);
  const save = async () => {
    try {
      if (editClient) await api.put(`/clients/${editClient.id}`, f);
      else await api.post("/clients", f);
      toast.success("Klien disimpan"); setOpen(false); onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || <Button variant="outline" data-testid="add-client-btn"><Building2 className="h-4 w-4 mr-2" />{t("users.newClient")}</Button>}</DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{editClient ? t("common.edit") : t("users.newClient")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>{t("cf.companyName")}</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="client-name" /></div>
          <div className="space-y-1.5"><Label>{t("common.code")}</Label><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="client-code" /></div>
          <div className="space-y-1.5"><Label>{t("common.phone")}</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="0812xxxxxxx" data-testid="client-phone" /></div>
          <div className="space-y-1.5"><Label>{t("cf.npwp")}</Label><Input value={f.npwp} onChange={(e) => setF({ ...f, npwp: e.target.value })} placeholder="00.000.000.0-000.000" data-testid="client-npwp" /></div>
          <div className="space-y-1.5"><Label>{t("cf.address")}</Label><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="client-address" /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="client-save">{t("common.save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
